import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Play, Loader2, CheckCircle2, Pause } from "lucide-react";
import AutoAgentTour from "@/components/AutoAgentTour";

export default function Autonomous() {
  const [s, setS] = useState({ enabled: false, daily_limit: 3, min_match_score: 70, auto_generate_proposals: true });
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [costs, setCosts] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/autonomous/settings").then(r => setS(v => ({ ...v, ...r.data }))).catch(() => {}),
      api.get("/autonomous/runs").then(r => setRuns(r.data)).catch(() => {}),
      api.get("/credits/status").then(r => setCosts(r.data.costs)).catch(() => {}),
    ]).finally(() => setLoaded(true));
  }, []);

  // Persist the current settings back to the server. Returns fresh backend state.
  const persist = async (next) => {
    const payload = {
      enabled: !!next.enabled,
      daily_limit: Number(next.daily_limit),
      min_match_score: Number(next.min_match_score),
      auto_generate_proposals: !!next.auto_generate_proposals,
    };
    const r = await api.put("/autonomous/settings", payload);
    return r.data;
  };

  const toggleEnabled = async () => {
    const next = { ...s, enabled: !s.enabled };
    setS(next);
    setSavingToggle(true);
    try {
      const server = await persist(next);
      setS(v => ({ ...v, ...server }));
      toast.success(next.enabled ? "Autonomous agent enabled" : "Autonomous agent paused");
    } catch (e) {
      // roll back
      setS(v => ({ ...v, enabled: !next.enabled }));
      toast.error(e?.response?.data?.detail || "Could not update agent state");
    } finally { setSavingToggle(false); }
  };

  const saveSettings = async () => {
    try {
      const server = await persist(s);
      setS(v => ({ ...v, ...server }));
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const run = async () => {
    // Guard: make sure we've persisted enabled=true before hitting run
    if (!s.enabled) {
      toast.error("Enable the agent first");
      return;
    }
    setRunning(true);
    try {
      // Always sync latest settings right before running so any slider change is honored.
      await persist(s);
      const r = await api.post("/autonomous/run");
      toast.success(`Processed ${r.data.processed} opportunities`);
      const rr = await api.get("/autonomous/runs");
      setRuns(rr.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="autonomous-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="bf-eyebrow inline-block text-amber-600 mb-3">AUTONOMOUS AGENT</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">
            24/7 <span className="bf-grad-text">proposal engine</span>.
          </h1>
          <p className="text-slate-500 mt-3">Configure how many proposals BidForge drafts every day, unattended.</p>
        </div>
        <AutoAgentTour costs={costs} />
      </div>

      <div className={`rounded-2xl bg-white border p-6 mb-6 transition-all ${s.enabled ? "border-emerald-300 shadow-[0_20px_60px_-15px_rgba(16,185,129,0.25)]" : "border-slate-200"}`}>
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${s.enabled ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg" : "bg-slate-100 text-slate-400"}`}>
              {s.enabled ? <Zap className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
            </div>
            <div>
              <div className="font-display font-bold text-xl text-slate-900" data-testid="agent-state-label">
                {s.enabled ? "Agent is ACTIVE" : "Agent is paused"}
              </div>
              <div className="text-sm text-slate-500">
                {s.enabled ? `Preparing up to ${s.daily_limit} proposals per day` : "Toggle on to enable autonomous mode"}
              </div>
            </div>
          </div>
          <button
            onClick={toggleEnabled}
            disabled={savingToggle || !loaded}
            className={`px-5 py-2.5 rounded-full font-display font-bold text-xs tracking-widest uppercase transition-all inline-flex items-center gap-2 disabled:opacity-50 ${s.enabled ? "bg-rose-50 text-rose-600 border-2 border-rose-200 hover:bg-rose-100" : "bf-pill"}`}
            data-testid="toggle-agent"
          >
            {savingToggle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {s.enabled ? "PAUSE AGENT" : "ENABLE AGENT"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8 pt-4 border-t border-slate-100">
          <div>
            <div className="text-xs font-mono font-bold tracking-widest text-slate-500 mb-2">
              Daily proposal limit: <span className="text-violet-600 font-bold text-sm">{s.daily_limit}</span>
            </div>
            <input type="range" min="0" max="10" value={s.daily_limit} onChange={(e) => setS({ ...s, daily_limit: Number(e.target.value) })} className="w-full accent-violet-500" data-testid="slider-limit" />
            <div className="flex justify-between text-[10px] font-mono text-slate-400 mt-1"><span>0</span><span>5</span><span>10</span></div>
          </div>
          <div>
            <div className="text-xs font-mono font-bold tracking-widest text-slate-500 mb-2">
              Minimum match score: <span className="text-violet-600 font-bold text-sm">{s.min_match_score}</span>
            </div>
            <input type="range" min="50" max="95" step="5" value={s.min_match_score} onChange={(e) => setS({ ...s, min_match_score: Number(e.target.value) })} className="w-full accent-violet-500" data-testid="slider-score" />
            <div className="flex justify-between text-[10px] font-mono text-slate-400 mt-1"><span>50</span><span>75</span><span>95</span></div>
          </div>
        </div>

        <label className="flex items-center gap-3 mt-6 cursor-pointer">
          <input type="checkbox" checked={s.auto_generate_proposals} onChange={(e) => setS({ ...s, auto_generate_proposals: e.target.checked })} className="w-4 h-4 accent-violet-500" data-testid="check-auto-gen" />
          <span className="text-sm text-slate-700">Auto-generate full proposals (not just bid analysis)</span>
        </label>

        <div className="flex gap-3 mt-6 flex-wrap">
          <button onClick={saveSettings} className="bf-pill-ghost px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold" data-testid="save-agent">
            Save settings
          </button>
          <button
            onClick={run}
            disabled={running || !s.enabled}
            className="bf-pill px-6 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="run-agent"
            title={!s.enabled ? "Enable the agent first" : "Run the autonomous cycle now"}
          >
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />RUNNING</> : <><Play className="w-4 h-4" />RUN NOW</>}
          </button>
        </div>

        {!s.enabled && loaded && (
          <div className="mt-4 text-xs text-slate-500 flex items-start gap-2">
            <span className="mt-0.5">↑</span>
            <span>The agent must be enabled before you can run it. Click <b>Enable Agent</b> above — it saves and turns on instantly.</span>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 text-xs font-mono font-bold tracking-widest text-fuchsia-600">RUN HISTORY</div>
        {runs.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No runs yet.</div>
        ) : runs.map((r, i) => (
          <div key={i} className="p-4 border-b border-slate-100 flex items-center justify-between last:border-b-0" data-testid={`run-row-${i}`}>
            <div>
              <div className="font-mono text-sm text-slate-800">{new Date(r.run_at).toLocaleString()}</div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400 mt-1">{r.processed_count} processed</div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <CheckCircle2 className="w-3 h-3" />SUCCESS
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
